import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { Customer, Nullable } from 'corgi-types';
import type { LeftProps, PositionProps, TopProps, ZIndexProps } from 'styled-system';
import { left, position, themeGet, top, zIndex } from 'styled-system';

import { CaptionRegularCR1 } from 'common/components/Typography';
import { TOOLTIP_CONTAINER_BOTTOM_Z_INDEX } from 'common/constants/z-index';
import { padding, space } from 'common/theme/helper';
import type { TransformProps } from 'common/theme/system-utilities';
import { transform } from 'common/theme/system-utilities';
import LogoutBlock from './LogoutBlock';
import MainBlock from './MainBlock';
import UpperBlock from './UpperBlock';

interface TooltipProps {
  customer?: Nullable<Customer>;
}

const TooltipContainer = styled.div<
  PositionProps & TopProps & LeftProps & TransformProps & ZIndexProps
>`
  ${position};
  ${top};
  ${left};
  ${transform};
  ${zIndex};
`;

const tooltipContainerStyles: ComponentProps<typeof TooltipContainer> = {
  position: 'absolute',
  top: '100%',
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: TOOLTIP_CONTAINER_BOTTOM_Z_INDEX,
};

const TooltipInnerContainer = styled.div`
  position: relative;
  width: ${space(10)};

  margin-top: 12px;

  ${padding(3)};

  background-color: ${themeGet('colors.sys.neutral.background.default')};

  border: ${themeGet('borderWidths.small')} solid ${themeGet('colors.sys.neutral.border.inactive')};
  border-radius: 3px;
  box-shadow: 0 3px 4px 1px ${themeGet('colors.overlay.light')};

  box-sizing: content-box;

  &::after {
    content: ' ';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%) translateY(-50%) rotate(45deg);

    width: 12px;
    height: 12px;

    background-color: ${themeGet('colors.sys.neutral.background.default')};
    border: ${themeGet('borderWidths.small')} solid
      ${themeGet('colors.sys.neutral.border.inactive')};

    border-bottom-color: transparent;
    border-right-color: transparent;
  }
`;

const TooltipBlock = styled(CaptionRegularCR1)`
  padding: 8px 0;

  &:first-of-type {
    padding-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
  }

  &:not(:last-child) {
    border-bottom: ${themeGet('borderWidths.small')} solid
      ${themeGet('colors.sys.neutral.border.weak')};
  }
`;

const Tooltip = ({ customer }: TooltipProps) => (
  <TooltipContainer {...tooltipContainerStyles} data-testid="my-account-tooltip">
    <TooltipInnerContainer>
      <TooltipBlock>
        <UpperBlock customer={customer} />
      </TooltipBlock>
      <TooltipBlock>
        <MainBlock />
      </TooltipBlock>
      {customer?.isLoggedIn && (
        <TooltipBlock>
          <LogoutBlock firstName={customer.firstName} />
        </TooltipBlock>
      )}
    </TooltipInnerContainer>
  </TooltipContainer>
);

export default Tooltip;
